// =============================================================
// lodgeHost.js — which app a browser address gets. SELF-CONTAINED / REMOVABLE.
//
// The Lodge app has its OWN address (lodge.harvus.com) so outfitters never load
// the address where Harvus's internal Azure AD session lives — each origin gets
// its own browser storage. On a Lodge host the Harvus app is never rendered:
//   /           → operator console
//   /o, /o/...  → operator console (old paths keep working)
//   /guide      → guide portal
//   /book/...   → public booking
//   /portal/... → client portal
// On the Harvus host the old paths still render the Lodge surfaces, and — once
// REACT_APP_LODGE_ORIGIN is set at build time (only after the domain is live) —
// the SIGN-IN surfaces (/o, /guide) move to the Lodge address. /book and
// /portal are not moved: links already sent to guests keep working where they
// are. Harvus users keep the in-app Lodge console behind the product switcher.
// Pure: no window access — callers pass the location in.
// =============================================================

export const DEFAULT_LODGE_HOSTS = ['lodge.harvus.com'];

export function lodgeHosts(extra = '') {
  const more = String(extra || '').split(',').map((h) => h.trim().toLowerCase()).filter(Boolean);
  return Array.from(new Set([...DEFAULT_LODGE_HOSTS, ...more]));
}

export function isLodgeHost(hostname, extra = '') {
  return lodgeHosts(extra).includes(String(hostname || '').toLowerCase());
}

const under = (p, prefix) => p === prefix || p.startsWith(`${prefix}/`);

// 'operator' | 'book' | 'portal' | 'guide' | null (null = the Harvus app).
export function surfaceFor({ hostname, pathname, extraHosts = '' }) {
  const p = pathname || '/';
  if (p.startsWith('/book/')) return 'book';
  if (p.startsWith('/portal/')) return 'portal';
  if (under(p, '/guide')) return 'guide';
  if (under(p, '/o')) return 'operator';
  // A Lodge host never falls through to the Harvus app.
  return isLodgeHost(hostname, extraHosts) ? 'operator' : null;
}

// Where to send a sign-in surface on the Harvus host, or null to stay. The
// fragment is kept (a sign-in ticket may be riding on it).
export function lodgeRedirectFor({ hostname, pathname, search = '', hash = '', lodgeOrigin = '', extraHosts = '' }) {
  if (!lodgeOrigin || isLodgeHost(hostname, extraHosts)) return null;
  let origin;
  try {
    const u = new URL(lodgeOrigin);
    if (u.protocol !== 'https:' || !isLodgeHost(u.hostname, extraHosts)) return null; // only ever to a Lodge host
    origin = u.origin;
  } catch { return null; }
  const p = pathname || '/';
  if (under(p, '/o')) return `${origin}${p.slice(2) || '/'}${search}${hash}`;
  if (under(p, '/guide')) return `${origin}${p}${search}${hash}`;
  return null;
}
